import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, ArrowUp, ArrowDown, ArrowRight, Trophy, Play, RotateCcw, Volume2, VolumeX } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Responsive grid size - bigger on PC
const getGridSize = () => {
  if (typeof window !== 'undefined' && window.innerWidth >= 768) {
    return 26; // Plus grand sur PC
  }
  return 22; // Mobile
};

const getCellSize = () => {
  if (typeof window !== 'undefined' && window.innerWidth >= 768) {
    return 20; // Cellules plus grandes sur PC
  }
  return 18; // Mobile
};

const INITIAL_SPEED = 160; // Vitesse plus lente (était 120)
const SLOW_START_SPEED = 350; // Démarrage encore plus lent (était 280)
const SLOW_START_DURATION = 3000; // 3 secondes de démarrage lent
const MUSIC_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_2865d04f-1753-4ac2-b4d4-74d332d7faa7/artifacts/7b3d3891a395efa2_kaazoom-cherry-blossom-gardens-2-min-edit-peaceful-japanese-music-369418.mp3";

// Background elements - Night sakura theme
const generateBackgroundElements = (gridSize, cellSize) => {
  const elements = [];
  const canvasWidth = gridSize * cellSize;
  const canvasHeight = gridSize * cellSize;
  
  // Stars
  for (let i = 0; i < 40; i++) {
    elements.push({
      type: 'star',
      x: Math.random() * canvasWidth,
      y: Math.random() * canvasHeight * 0.6, // Upper part only
      size: 1 + Math.random() * 2,
      twinkle: Math.random() * Math.PI * 2
    });
  }
  
  // Falling petals (pink sakura petals)
  for (let i = 0; i < 20; i++) {
    elements.push({
      type: 'petal',
      x: Math.random() * canvasWidth,
      y: Math.random() * canvasHeight,
      size: 4 + Math.random() * 6,
      rotation: Math.random() * Math.PI * 2,
      speed: 0.3 + Math.random() * 0.5,
      sway: Math.random() * Math.PI * 2
    });
  }
  
  return elements;
};

let petalAnimationFrame = 0;
let moonPulseFrame = 0;

const SnakeSakura = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const audioRef = useRef(null);
  const collectSoundRef = useRef(null);
  const gameStartTimeRef = useRef(null);
  const backgroundElementsRef = useRef(null);
  
  const [gameState, setGameState] = useState("idle"); // idle, playing, gameover
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [snake, setSnake] = useState([{ x: 10, y: 10 }]);
  const [direction, setDirection] = useState({ x: 1, y: 0 });
  const [droplet, setDroplet] = useState({ x: 15, y: 10 });
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [isMuted, setIsMuted] = useState(false);
  const [isSlowStart, setIsSlowStart] = useState(true);
  const [controlMode, setControlMode] = useState("buttons"); // "buttons" ou "swipe"
  const touchStartRef = useRef({ x: 0, y: 0 });
  
  // Responsive grid size
  const [GRID_SIZE, setGridSize] = useState(getGridSize());
  const [CELL_SIZE, setCellSize] = useState(getCellSize());
  
  // Update grid size on window resize
  useEffect(() => {
    const handleResize = () => {
      setGridSize(getGridSize());
      setCellSize(getCellSize());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize audio on component mount
  useEffect(() => {
    audioRef.current = new Audio(MUSIC_URL);
    audioRef.current.loop = true;
    audioRef.current.volume = 0.4;
    
    // Create collect sound using Web Audio API (water drop sound)
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    collectSoundRef.current = audioCtx;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Initialize background elements when grid size changes
  useEffect(() => {
    backgroundElementsRef.current = generateBackgroundElements(GRID_SIZE, CELL_SIZE);
  }, [GRID_SIZE, CELL_SIZE]);

  // Play collect sound
  const playCollectSound = () => {
    if (isMuted || !collectSoundRef.current) return;
    try {
      const ctx = collectSoundRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.setValueAtTime(800, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
      oscillator.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.2);
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      console.log("Sound error:", e);
    }
  };

  // Handle music play/pause based on game state
  useEffect(() => {
    if (!audioRef.current) return;
    
    if (gameState === "playing" && !isMuted) {
      audioRef.current.play().catch(e => console.log("Audio play prevented:", e));
    } else if (gameState !== "playing") {
      // Keep music playing even on game over, stop only when leaving page
    }
  }, [gameState, isMuted]);

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.play().catch(e => console.log("Audio play prevented:", e));
      } else {
        audioRef.current.pause();
      }
    }
    setIsMuted(!isMuted);
  };

  // Fetch leaderboard and personal best
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leaderboardRes, myScoreRes] = await Promise.all([
          axios.get(`${API}/snake/leaderboard`),
          axios.get(`${API}/snake/my-score`, { withCredentials: true })
        ]);
        setLeaderboard(leaderboardRes.data || []);
        setHighScore(myScoreRes.data?.score || 0);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Generate random droplet position
  const generateDroplet = useCallback((currentSnake) => {
    let newDroplet;
    do {
      newDroplet = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
    } while (currentSnake.some(segment => segment.x === newDroplet.x && segment.y === newDroplet.y));
    return newDroplet;
  }, []);

  // Start game
  const startGame = useCallback(() => {
    const initialSnake = [{ x: 10, y: 10 }];
    setSnake(initialSnake);
    setDirection({ x: 1, y: 0 });
    setDroplet(generateDroplet(initialSnake));
    setScore(0);
    setSpeed(SLOW_START_SPEED); // Démarrage lent
    setIsSlowStart(true);
    gameStartTimeRef.current = Date.now();
    setGameState("playing");
    
    // Après 3 secondes, passer à la vitesse normale
    setTimeout(() => {
      setIsSlowStart(false);
      setSpeed(INITIAL_SPEED);
    }, SLOW_START_DURATION);
    
    // Start music immediately on user click (bypasses autoplay restrictions)
    if (audioRef.current && !isMuted) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play error:", e));
    }
  }, [generateDroplet, isMuted]);

  // Submit score
  const submitScore = useCallback(async (finalScore) => {
    try {
      await axios.post(`${API}/snake/score`, { score: finalScore }, { withCredentials: true });
      // Refresh leaderboard
      const leaderboardRes = await axios.get(`${API}/snake/leaderboard`);
      setLeaderboard(leaderboardRes.data || []);
      if (finalScore > highScore) {
        setHighScore(finalScore);
      }
    } catch (error) {
      console.error("Error submitting score:", error);
    }
  }, [highScore]);

  // Game over
  const gameOver = useCallback((finalScore) => {
    setGameState("gameover");
    submitScore(finalScore);
  }, [submitScore]);

  // Game loop
  useEffect(() => {
    if (gameState !== "playing") return;

    const moveSnake = () => {
      setSnake(prevSnake => {
        const head = prevSnake[0];
        const newHead = {
          x: head.x + direction.x,
          y: head.y + direction.y
        };

        // Check wall collision
        if (newHead.x < 0 || newHead.x >= GRID_SIZE || newHead.y < 0 || newHead.y >= GRID_SIZE) {
          gameOver(score);
          return prevSnake;
        }

        // Check self collision
        if (prevSnake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
          gameOver(score);
          return prevSnake;
        }

        const newSnake = [newHead, ...prevSnake];

        // Check droplet collection
        if (newHead.x === droplet.x && newHead.y === droplet.y) {
          playCollectSound();
          setScore(prev => {
            const newScore = prev + 1;
            // Increase speed every 5 points (mais moins agressif)
            if (newScore % 5 === 0) {
              setSpeed(prev => Math.max(prev - 5, 80)); // Réduction plus douce, vitesse min 80 (était 50)
            }
            return newScore;
          });
          setDroplet(generateDroplet(newSnake));
        } else {
          newSnake.pop();
        }

        return newSnake;
      });
    };

    gameLoopRef.current = setInterval(moveSnake, speed);
    return () => clearInterval(gameLoopRef.current);
  }, [gameState, direction, droplet, score, speed, generateDroplet, gameOver]);

  // Animation loop for background (continuous, even when game is idle)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    let animationId;
    
    const animate = () => {
      const ctx = canvas.getContext("2d");
      const width = GRID_SIZE * CELL_SIZE;
      const height = GRID_SIZE * CELL_SIZE;

      // Increment animation frames for petal movement and moon pulse
      petalAnimationFrame += 0.03; // Faster petal animation
      moonPulseFrame += 0.05; // Moon luminosity pulse

      // Draw night sky gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, height);
      gradient.addColorStop(0, "#0c0a1d"); // Dark purple-black top
      gradient.addColorStop(0.4, "#1a1a2e"); // Dark blue
      gradient.addColorStop(0.7, "#16213e"); // Darker blue
      gradient.addColorStop(1, "#1a1a2e"); // Dark bottom
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Draw mountains silhouette
      ctx.fillStyle = "#0f0f1a";
      ctx.beginPath();
      ctx.moveTo(0, height * 0.75);
      ctx.lineTo(width * 0.15, height * 0.55);
      ctx.lineTo(width * 0.3, height * 0.65);
      ctx.lineTo(width * 0.5, height * 0.45);
      ctx.lineTo(width * 0.65, height * 0.6);
      ctx.lineTo(width * 0.8, height * 0.5);
      ctx.lineTo(width, height * 0.7);
      ctx.lineTo(width, height);
      ctx.lineTo(0, height);
      ctx.closePath();
      ctx.fill();

      // Draw moon with pulsating luminosity
      const moonX = width * 0.75;
      const moonY = height * 0.18;
      const moonRadius = 18;
      
      // Pulsating glow intensity (varies between 0.15 and 0.45)
      const moonGlowIntensity = 0.3 + Math.sin(moonPulseFrame) * 0.15;
      const moonGlowSize = moonRadius * (2.5 + Math.sin(moonPulseFrame * 0.5) * 0.5);
      
      // Moon outer glow (pulsating)
      const moonGlow = ctx.createRadialGradient(moonX, moonY, moonRadius * 0.5, moonX, moonY, moonGlowSize);
      moonGlow.addColorStop(0, `rgba(255, 255, 255, ${moonGlowIntensity})`);
      moonGlow.addColorStop(0.5, `rgba(255, 255, 255, ${moonGlowIntensity * 0.4})`);
      moonGlow.addColorStop(1, "rgba(255, 255, 255, 0)");
      ctx.fillStyle = moonGlow;
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonGlowSize, 0, Math.PI * 2);
      ctx.fill();
      
      // Secondary softer glow ring
      const moonGlow2 = ctx.createRadialGradient(moonX, moonY, moonRadius, moonX, moonY, moonRadius * 4);
      moonGlow2.addColorStop(0, `rgba(200, 200, 255, ${moonGlowIntensity * 0.2})`);
      moonGlow2.addColorStop(1, "rgba(200, 200, 255, 0)");
      ctx.fillStyle = moonGlow2;
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonRadius * 4, 0, Math.PI * 2);
      ctx.fill();
      
      // Moon body with slight brightness variation
      const moonBrightness = 245 + Math.sin(moonPulseFrame * 2) * 10;
      const moonBodyGradient = ctx.createRadialGradient(moonX - 5, moonY - 5, 0, moonX, moonY, moonRadius);
      moonBodyGradient.addColorStop(0, `rgb(${moonBrightness}, ${moonBrightness}, ${moonBrightness})`);
      moonBodyGradient.addColorStop(0.7, `rgb(${moonBrightness - 10}, ${moonBrightness - 10}, ${moonBrightness - 5})`);
      moonBodyGradient.addColorStop(1, `rgb(${moonBrightness - 30}, ${moonBrightness - 30}, ${moonBrightness - 20})`);
      ctx.fillStyle = moonBodyGradient;
      ctx.beginPath();
      ctx.arc(moonX, moonY, moonRadius, 0, Math.PI * 2);
      ctx.fill();

      // Draw stars
      const bgElements = backgroundElementsRef.current || [];
      bgElements.forEach(el => {
        if (el.type === 'star') {
          const twinkle = 0.5 + 0.5 * Math.sin(petalAnimationFrame * 2 + el.twinkle);
          ctx.fillStyle = `rgba(255, 255, 255, ${twinkle * 0.8})`;
          ctx.beginPath();
          ctx.arc(el.x, el.y, el.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Draw sakura tree silhouette in corner
      ctx.strokeStyle = "#1a1a2e";
      ctx.fillStyle = "#0a0a12";
      ctx.lineWidth = 3;
      
      // Main trunk from bottom left
      ctx.beginPath();
      ctx.moveTo(-10, height + 10);
      ctx.bezierCurveTo(20, height * 0.7, 30, height * 0.5, 50, height * 0.35);
      ctx.lineTo(45, height * 0.35);
      ctx.bezierCurveTo(25, height * 0.5, 15, height * 0.7, -15, height + 10);
      ctx.fill();
      
      // Branches
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#1f1f2e";
      ctx.beginPath();
      ctx.moveTo(50, height * 0.35);
      ctx.bezierCurveTo(70, height * 0.25, 100, height * 0.2, 130, height * 0.15);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(45, height * 0.4);
      ctx.bezierCurveTo(80, height * 0.35, 110, height * 0.3, 150, height * 0.25);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(40, height * 0.5);
      ctx.bezierCurveTo(60, height * 0.45, 90, height * 0.4, 120, height * 0.38);
      ctx.stroke();

      // Draw falling petals (animated with more dynamic movement)
      bgElements.forEach(el => {
        if (el.type === 'petal') {
          // More dynamic vertical movement - petals fall continuously
          const fallSpeed = el.speed * 150;
          const animY = (el.y + petalAnimationFrame * fallSpeed) % (height + 30) - 10;
          
          // Horizontal sway with varying amplitude
          const swayAmplitude = 20 + el.size * 2;
          const swayX = el.x + Math.sin(petalAnimationFrame * 1.5 + el.sway) * swayAmplitude;
          
          // Rotation animation
          const rotation = el.rotation + petalAnimationFrame * (0.5 + el.speed);
          
          // Slight opacity variation for depth effect
          const opacity = 0.7 + Math.sin(petalAnimationFrame + el.sway) * 0.3;
          
          ctx.save();
          ctx.translate(swayX, animY);
          ctx.rotate(rotation);
          ctx.globalAlpha = opacity;
          
          // Petal gradient with more vibrant colors
          const petalGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, el.size);
          petalGradient.addColorStop(0, "#ffb6c1"); // Light pink center
          petalGradient.addColorStop(0.5, "#ff69b4"); // Hot pink
          petalGradient.addColorStop(1, "#ec4899"); // Pink edge
          ctx.fillStyle = petalGradient;
          
          // Draw petal shape (more organic)
          ctx.beginPath();
          ctx.ellipse(0, 0, el.size, el.size * 0.6, 0, 0, Math.PI * 2);
          ctx.fill();
          
          // Petal highlight (shiny effect)
          ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
          ctx.beginPath();
          ctx.ellipse(-el.size * 0.3, -el.size * 0.15, el.size * 0.35, el.size * 0.2, -0.3, 0, Math.PI * 2);
          ctx.fill();
          
          ctx.globalAlpha = 1;
          ctx.restore();
        }
      });

      // Draw subtle grid (very faint)
      ctx.strokeStyle = "rgba(255, 255, 255, 0.03)";
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= GRID_SIZE; i++) {
        ctx.beginPath();
        ctx.moveTo(i * CELL_SIZE, 0);
        ctx.lineTo(i * CELL_SIZE, height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, i * CELL_SIZE);
        ctx.lineTo(width, i * CELL_SIZE);
        ctx.stroke();
      }

      // Draw water droplet (glowing blue)
      const dx = droplet.x * CELL_SIZE + CELL_SIZE / 2;
      const dy = droplet.y * CELL_SIZE + CELL_SIZE / 2;
      
      ctx.save();
      // Droplet glow
      const glowGradient = ctx.createRadialGradient(dx, dy, 0, dx, dy, 15);
      glowGradient.addColorStop(0, "rgba(59, 130, 246, 0.5)");
      glowGradient.addColorStop(0.5, "rgba(59, 130, 246, 0.2)");
      glowGradient.addColorStop(1, "rgba(59, 130, 246, 0)");
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(dx, dy, 15, 0, Math.PI * 2);
      ctx.fill();
      
      // Droplet body (teardrop)
      const dropGradient = ctx.createRadialGradient(dx - 2, dy - 2, 0, dx, dy, 8);
      dropGradient.addColorStop(0, "#93c5fd");
      dropGradient.addColorStop(0.5, "#3b82f6");
      dropGradient.addColorStop(1, "#1d4ed8");
      ctx.fillStyle = dropGradient;
      
      ctx.beginPath();
      ctx.moveTo(dx, dy - 8);
      ctx.bezierCurveTo(dx - 7, dy - 2, dx - 7, dy + 6, dx, dy + 8);
      ctx.bezierCurveTo(dx + 7, dy + 6, dx + 7, dy - 2, dx, dy - 8);
      ctx.fill();
      
      // Shine highlight
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      ctx.beginPath();
      ctx.ellipse(dx - 2, dy - 2, 2, 3, -0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Draw snake (as dark branch with pink sakura flowers)
      snake.forEach((segment, index) => {
        const isHead = index === 0;
        const isTail = index === snake.length - 1;
        const x = segment.x * CELL_SIZE + CELL_SIZE / 2;
        const y = segment.y * CELL_SIZE + CELL_SIZE / 2;
        
        // Branch colors - natural brown wood
        ctx.save();
        
        // Draw branch segment
        const thickness = isHead ? CELL_SIZE - 4 : isTail ? CELL_SIZE - 8 : CELL_SIZE - 6;
        
        // Brown branch base with gradient
        const branchGradient = ctx.createLinearGradient(
          segment.x * CELL_SIZE, 
          segment.y * CELL_SIZE, 
          segment.x * CELL_SIZE + thickness, 
          segment.y * CELL_SIZE + thickness
        );
        branchGradient.addColorStop(0, "#5D4037"); // Dark brown
        branchGradient.addColorStop(0.5, "#795548"); // Medium brown
        branchGradient.addColorStop(1, "#4E342E"); // Darker brown edge
        ctx.fillStyle = branchGradient;
        ctx.beginPath();
        ctx.roundRect(
          segment.x * CELL_SIZE + (CELL_SIZE - thickness) / 2,
          segment.y * CELL_SIZE + (CELL_SIZE - thickness) / 2,
          thickness,
          thickness,
          thickness / 3
        );
        ctx.fill();
        
        // Light brown highlight on top (bark texture effect)
        ctx.fillStyle = "rgba(161, 136, 127, 0.4)"; // Light brown highlight
        ctx.beginPath();
        ctx.roundRect(
          segment.x * CELL_SIZE + (CELL_SIZE - thickness) / 2,
          segment.y * CELL_SIZE + (CELL_SIZE - thickness) / 2,
          thickness,
          thickness * 0.35,
          thickness / 4
        );
        ctx.fill();

        // Add cherry blossom on head
        if (isHead) {
          // Glowing pink flower on the branch tip
          const flowerGlow = ctx.createRadialGradient(x, y, 0, x, y, 12);
          flowerGlow.addColorStop(0, "rgba(236, 72, 153, 0.4)");
          flowerGlow.addColorStop(1, "rgba(236, 72, 153, 0)");
          ctx.fillStyle = flowerGlow;
          ctx.beginPath();
          ctx.arc(x, y, 12, 0, Math.PI * 2);
          ctx.fill();
          
          // Pink petals
          ctx.fillStyle = "#ec4899";
          for (let p = 0; p < 5; p++) {
            const angle = (p * Math.PI * 2) / 5 - Math.PI / 2;
            const px = x + Math.cos(angle) * 5;
            const py = y + Math.sin(angle) * 5;
            ctx.beginPath();
            ctx.ellipse(px, py, 4, 2.5, angle, 0, Math.PI * 2);
            ctx.fill();
          }
          // Flower center
          ctx.fillStyle = "#fbbf24";
          ctx.beginPath();
          ctx.arc(x, y, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Add small green leaf on some segments
        if (!isHead && !isTail && index % 4 === 0) {
          ctx.save();
          ctx.translate(x + 5, y - 3);
          ctx.rotate(-0.3); // Slight tilt
          
          // Leaf shape
          ctx.fillStyle = "#22c55e"; // Green
          ctx.beginPath();
          ctx.ellipse(0, 0, 5, 2.5, 0, 0, Math.PI * 2);
          ctx.fill();
          
          // Leaf vein (center line)
          ctx.strokeStyle = "#16a34a"; // Darker green
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(-4, 0);
          ctx.lineTo(4, 0);
          ctx.stroke();
          
          ctx.restore();
        }
        
        ctx.restore();
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [snake, droplet]);

  // Draw game (removed - now handled by animation loop above)

  // Keyboard controls - Arrows + QZSD (AZERTY)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block page scroll when using arrow keys during game
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)) {
        e.preventDefault();
      }
      
      if (gameState !== "playing") return;
      
      switch (e.key) {
        case "ArrowUp":
        case "z":
        case "Z":
          if (direction.y !== 1) setDirection({ x: 0, y: -1 });
          break;
        case "ArrowDown":
        case "s":
        case "S":
          if (direction.y !== -1) setDirection({ x: 0, y: 1 });
          break;
        case "ArrowLeft":
        case "q":
        case "Q":
          if (direction.x !== 1) setDirection({ x: -1, y: 0 });
          break;
        case "ArrowRight":
        case "d":
        case "D":
          if (direction.x !== -1) setDirection({ x: 1, y: 0 });
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState, direction]);

  // Touch controls
  const handleControl = (dir) => {
    if (gameState !== "playing") return;
    
    switch (dir) {
      case "up":
        if (direction.y !== 1) setDirection({ x: 0, y: -1 });
        break;
      case "down":
        if (direction.y !== -1) setDirection({ x: 0, y: 1 });
        break;
      case "left":
        if (direction.x !== 1) setDirection({ x: -1, y: 0 });
        break;
      case "right":
        if (direction.x !== -1) setDirection({ x: 1, y: 0 });
        break;
      default:
        break;
    }
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (gameState !== "playing" || controlMode !== "swipe") return;
    
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    
    const minSwipeDistance = 30; // Minimum distance pour détecter un swipe
    
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Swipe horizontal
      if (Math.abs(deltaX) > minSwipeDistance) {
        if (deltaX > 0) {
          handleControl("right");
        } else {
          handleControl("left");
        }
      }
    } else {
      // Swipe vertical
      if (Math.abs(deltaY) > minSwipeDistance) {
        if (deltaY > 0) {
          handleControl("down");
        } else {
          handleControl("up");
        }
      }
    }
  };

  const podiumColors = ["#fbbf24", "#9ca3af", "#cd7f32"]; // Gold, Silver, Bronze

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-100 to-pink-50 p-4">
      {/* Header */}
      <div className="max-w-lg mx-auto mb-4">
        <div className="flex justify-between items-center mb-2">
          <Button 
            variant="ghost" 
            onClick={() => {
              if (audioRef.current) audioRef.current.pause();
              navigate("/mon-compte");
            }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            onClick={toggleMute}
            className="border-pink-300"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5 text-pink-600" />
            ) : (
              <Volume2 className="w-5 h-5 text-pink-600" />
            )}
          </Button>
        </div>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold text-pink-600 mb-1">🌸 Snake Sakura</h1>
          <p className="text-sm text-pink-500">Sois le meilleur à aider l'arbre Sakura à s'épanouir et monte sur le podium !</p>
        </div>
      </div>

      {/* Podium */}
      <Card className="max-w-lg mx-auto mb-4 border-pink-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Trophy className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-semibold text-slate-700">Podium</h2>
          </div>
          
          {leaderboard.length > 0 ? (
            <div className="flex justify-center items-end gap-2">
              {/* 2nd place */}
              {leaderboard[1] && (
                <div className="text-center">
                  <div 
                    className="w-20 h-16 rounded-t-lg flex flex-col items-center justify-center"
                    style={{ backgroundColor: podiumColors[1] }}
                  >
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <p className="text-xs font-medium mt-1 truncate w-20">{leaderboard[1].display_name}</p>
                  <p className="text-xs text-pink-600">{leaderboard[1].score} pts</p>
                </div>
              )}
              
              {/* 1st place */}
              {leaderboard[0] && (
                <div className="text-center">
                  <div 
                    className="w-24 h-20 rounded-t-lg flex flex-col items-center justify-center"
                    style={{ backgroundColor: podiumColors[0] }}
                  >
                    <span className="text-2xl">👑</span>
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <p className="text-sm font-medium mt-1 truncate w-24">{leaderboard[0].display_name}</p>
                  <p className="text-sm text-pink-600 font-semibold">{leaderboard[0].score} pts</p>
                </div>
              )}
              
              {/* 3rd place */}
              {leaderboard[2] && (
                <div className="text-center">
                  <div 
                    className="w-20 h-12 rounded-t-lg flex flex-col items-center justify-center"
                    style={{ backgroundColor: podiumColors[2] }}
                  >
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <p className="text-xs font-medium mt-1 truncate w-20">{leaderboard[2].display_name}</p>
                  <p className="text-xs text-pink-600">{leaderboard[2].score} pts</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-500 text-sm">Aucun score pour le moment. Soyez le premier !</p>
          )}
        </CardContent>
      </Card>

      {/* Game Area */}
      <div className="max-w-xl mx-auto">
        <Card className="border-pink-200 overflow-visible">
          <CardContent className="p-4">
            {/* Score display */}
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm">
                <span className="text-slate-500">Score:</span>
                <span className="font-bold text-pink-600 ml-1">{score}</span>
              </div>
              <div className="text-sm">
                <span className="text-slate-500">Record:</span>
                <span className="font-bold text-pink-600 ml-1">{highScore}</span>
              </div>
            </div>

            {/* Canvas with swipe detection */}
            <div 
              className="relative flex justify-center"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <canvas
                ref={canvasRef}
                width={GRID_SIZE * CELL_SIZE}
                height={GRID_SIZE * CELL_SIZE}
                className="border-2 border-pink-300 rounded-lg touch-none"
              />
              
              {/* Swipe indicator when in swipe mode */}
              {controlMode === "swipe" && gameState === "playing" && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                  👆 Glissez pour diriger
                </div>
              )}
              
              {/* Overlay for idle/gameover */}
              {gameState !== "playing" && (
                <div className="absolute inset-0 flex items-center justify-center bg-pink-900/50 rounded-lg">
                  <div className="text-center">
                    {gameState === "gameover" && (
                      <div className="mb-4">
                        <p className="text-white text-xl font-bold mb-1">Game Over !</p>
                        <p className="text-pink-200">Score: {score}</p>
                      </div>
                    )}
                    <Button 
                      onClick={startGame}
                      className="bg-pink-500 hover:bg-pink-600"
                    >
                      {gameState === "idle" ? (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Jouer
                        </>
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Rejouer
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Control Mode Toggle */}
            <div className="mt-3 flex justify-center gap-2">
              <Button
                variant={controlMode === "buttons" ? "default" : "outline"}
                size="sm"
                onClick={() => setControlMode("buttons")}
                className={controlMode === "buttons" ? "bg-pink-500" : "border-pink-300"}
              >
                🎮 Boutons
              </Button>
              <Button
                variant={controlMode === "swipe" ? "default" : "outline"}
                size="sm"
                onClick={() => setControlMode("swipe")}
                className={controlMode === "swipe" ? "bg-pink-500" : "border-pink-300"}
              >
                👆 Swipe
              </Button>
            </div>

            {/* Touch Controls - Only show in buttons mode */}
            {controlMode === "buttons" && (
              <div className="mt-4 p-4 rounded-xl bg-gradient-to-b from-slate-800 to-slate-900 border border-pink-500/30">
                <div className="flex flex-col items-center gap-3">
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-20 h-20 p-0 border-pink-400/50 bg-slate-700/80 hover:bg-pink-900/50 active:bg-pink-800"
                    onClick={() => handleControl("up")}
                    disabled={gameState !== "playing"}
                  >
                    <ArrowUp className="w-10 h-10 text-pink-300" />
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-20 h-20 p-0 border-pink-400/50 bg-slate-700/80 hover:bg-pink-900/50 active:bg-pink-800"
                      onClick={() => handleControl("left")}
                      disabled={gameState !== "playing"}
                    >
                      <ArrowLeft className="w-10 h-10 text-pink-300" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-20 h-20 p-0 border-pink-400/50 bg-slate-700/80 hover:bg-pink-900/50 active:bg-pink-800"
                      onClick={() => handleControl("down")}
                      disabled={gameState !== "playing"}
                    >
                      <ArrowDown className="w-10 h-10 text-pink-300" />
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      className="w-20 h-20 p-0 border-pink-400/50 bg-slate-700/80 hover:bg-pink-900/50 active:bg-pink-800"
                      onClick={() => handleControl("right")}
                      disabled={gameState !== "playing"}
                    >
                      <ArrowRight className="w-10 h-10 text-pink-300" />
                    </Button>
                  </div>
                  <p className="text-xs text-pink-300/70 mt-1">🌸 Guide la branche vers les gouttes de rosée</p>
                </div>
              </div>
            )}
            
            {/* Swipe instructions */}
            {controlMode === "swipe" && (
              <div className="mt-4 p-4 rounded-xl bg-gradient-to-b from-slate-800 to-slate-900 border border-pink-500/30 text-center">
                <p className="text-pink-300 text-sm">👆 Glissez votre doigt sur l'écran de jeu</p>
                <p className="text-pink-300/60 text-xs mt-1">Haut, Bas, Gauche, Droite</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SnakeSakura;
